/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Inject, Injectable, InjectionToken} from '@angular/core';

import {DOCUMENT} from '../dom_tokens';

import {EventManagerPlugin} from './event_manager';

const /** @type {?} */ EVENT_NAMES = {
  // pan
  'pan': true,
  'panstart': true,
  'panmove': true,
  'panend': true,
  'pancancel': true,
  'panleft': true,
  'panright': true,
  'panup': true,
  'pandown': true,
  // pinch
  'pinch': true,
  'pinchstart': true,
  'pinchmove': true,
  'pinchend': true,
  'pinchcancel': true,
  'pinchin': true,
  'pinchout': true,
  // press
  'press': true,
  'pressup': true,
  // rotate
  'rotate': true,
  'rotatestart': true,
  'rotatemove': true,
  'rotateend': true,
  'rotatecancel': true,
  // swipe
  'swipe': true,
  'swipeleft': true,
  'swiperight': true,
  'swipeup': true,
  'swipedown': true,
  // tap
  'tap': true,
};
/**
 * A DI token that you can use to provide{\@link HammerGestureConfig} to Angular. Use it to configure
 * Hammer gestures.
 * 
 * \@experimental
 */
export const HAMMER_GESTURE_CONFIG = new InjectionToken<HammerGestureConfig>('HammerGestureConfig');

export interface HammerInstance {
  on(eventName: string, callback?: Function): void;
  off(eventName: string, callback?: Function): void;
}
/**
 * \@experimental
 */
export class HammerGestureConfig {
  events: string[] = [];

  overrides: {[key: string]: Object} = {};
/**
 * @param {?} element
 * @return {?}
 */
buildHammer(element: HTMLElement): HammerInstance {
    const /** @type {?} */ mc = new Hammer(element);

    mc.get('pinch').set({enable: true});
    mc.get('rotate').set({enable: true});

    for (const /** @type {?} */ eventName in this.overrides) {
      mc.get(eventName).set(this.overrides[eventName]);
    }

    return mc;
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function HammerGestureConfig_tsickle_Closure_declarations() {
/** @type {?} */
HammerGestureConfig.decorators;
/**
 * @nocollapse
 * @type {?}
 */
HammerGestureConfig.ctorParameters;
/** @type {?} */
HammerGestureConfig.prototype.events;
/** @type {?} */
HammerGestureConfig.prototype.overrides;
}

export class HammerGesturesPlugin extends EventManagerPlugin {
/**
 * @param {?} doc
 * @param {?} _config
 */
constructor(
       doc: any,
private _config: HammerGestureConfig) {
    super(doc);
  }
/**
 * @param {?} eventName
 * @return {?}
 */
supports(eventName: string): boolean {
    if (!EVENT_NAMES.hasOwnProperty(eventName.toLowerCase()) && !this.isCustomEvent(eventName)) {
      return false;
    }

    if (!( /** @type {?} */((window as any))).Hammer) {
      throw new Error(`Hammer.js is not loaded, can not bind ${eventName} event`);
    }

    return true;
  }
/**
 * @param {?} element
 * @param {?} eventName
 * @param {?} handler
 * @return {?}
 */
addEventListener(element: HTMLElement, eventName: string, handler: Function): Function {
    const /** @type {?} */ zone = this.manager.getZone();
    eventName = eventName.toLowerCase();

    return zone.runOutsideAngular(() => {
      // Creating the manager bind events, must be done outside of angular
      const /** @type {?} */ mc = this._config.buildHammer(element);
      const /** @type {?} */ callback = function(eventObj: HammerInput) {
        zone.runGuarded(function() { handler(eventObj); });
      };
      mc.on(eventName, callback);
      return () => mc.off(eventName, callback);
    });
  }
/**
 * @param {?} eventName
 * @return {?}
 */
isCustomEvent(eventName: string): boolean { return this._config.events.indexOf(eventName) > -1; }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Inject, args: [DOCUMENT, ] }, ]},
{type: HammerGestureConfig, decorators: [{ type: Inject, args: [HAMMER_GESTURE_CONFIG, ] }, ]},
];
}

function HammerGesturesPlugin_tsickle_Closure_declarations() {
/** @type {?} */
HammerGesturesPlugin.decorators;
/**
 * @nocollapse
 * @type {?}
 */
HammerGesturesPlugin.ctorParameters;
/** @type {?} */
HammerGesturesPlugin.prototype._config;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
